import type { ReactElement } from 'react'
import { Home, Check } from 'lucide-react'
import { AppShell } from '../components/AppShell.js'
import { Button } from '../components/ui/button.js'
import { Card } from '../components/ui/card.js'
import { SavedPicturesStrip } from '../components/SavedPicturesStrip.js'
import { useStep } from '../state/useStep.js'
import { useDemoStore } from '../state/store.js'
import { lookupModel } from '../lib/modelLookup.js'

const Row = ({ label, value, note }: { label: string; value: string; note: string }): ReactElement => (
  <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
    <div className="w-8 h-8 rounded-md bg-accent text-accent-foreground flex items-center justify-center">
      <Home className="h-4 w-4" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-semibold">{label}</div>
      <div className="text-sm text-muted-foreground">{value}</div>
    </div>
    <div className="text-xs text-[color:var(--color-ok-500)] text-right max-w-[10rem] inline-flex items-center gap-1">
      {note} <Check className="h-3 w-3" />
    </div>
  </div>
)

export const ModelLookupScreen = (): ReactElement => {
  const { goto } = useStep()
  const { state } = useDemoStore()
  const brand = state.modelLabel?.brand ?? ''
  const modelNumber = state.modelLabel?.model ?? ''
  const specs = lookupModel(brand, modelNumber)

  const goBack = (): void => goto('scan-model-label')

  return (
    <AppShell
      title="Model lookup results"
      showBack onBack={goBack}
      progress={0.25}
      footer={
        <div className="space-y-2">
          <Button size="xl" onClick={() => goto('scan-countertop')}>That's my dishwasher — Continue</Button>
          <div className="text-center">
            <Button variant="link" onClick={goBack} className="text-muted-foreground">
              Not my model — go back
            </Button>
          </div>
        </div>
      }
    >
      <SavedPicturesStrip scan="model-label" />

      <div className="bg-muted rounded-2xl p-4 mb-5 flex items-center gap-4">
        <img
          src="/demo/whirlpool-dishwasher.png"
          alt={specs.displayName}
          className="h-20 w-auto shrink-0 object-contain bg-white rounded-lg p-1"
        />
        <div className="min-w-0">
          <h1 className="text-xl">{specs.displayName}</h1>
          <div className="text-sm text-muted-foreground">
            {specs.fromCatalog
              ? 'Built-in dishwasher — specs on file'
              : 'Built-in dishwasher — using a standard 24" profile until install'}
          </div>
          <div className="text-sm text-primary mt-2">
            {specs.fromCatalog
              ? 'Specs loaded from manufacturer database'
              : `Read from your label: ${brand} ${modelNumber}`.trim()}
          </div>
        </div>
      </div>

      <div className="text-xs font-semibold tracking-wider text-primary uppercase mb-2">
        {specs.fromCatalog ? 'Manufacturer specs' : 'Standard 24" profile'}
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        {specs.fromCatalog
          ? 'These are pre-filled from the model database — fewer questions for you.'
          : "We didn't have this exact model on file, so we're using a standard 24\" built-in profile. The installer will confirm the exact specs onsite."}
      </p>

      <Card className="p-5">
        <Row
          label="Required opening"
          value={`${specs.requiredOpening.widthInches}" W × ${specs.requiredOpening.heightInchesMin}–${specs.requiredOpening.heightInchesMax}" H × ${specs.requiredOpening.depthInches}" D`}
          note="Standard fit"
        />
        <Row
          label="Electrical"
          value={specs.electrical}
          note={specs.electrical === 'Hardwired (direct wire)' ? 'No plug required' : 'Plug needed'}
        />
        <Row
          label="Unit weight"
          value={`${specs.unitWeightLbs} lbs`}
          note={specs.needsExtraSupport ? 'Extra support recommended' : 'Standard — no extra support'}
        />
        <Row
          label="Door clearance"
          value={`${specs.doorClearanceInches}" projection when open`}
          note="Check for obstructions"
        />
        <Row
          label="Depth with door"
          value={`${specs.depthWithDoorInches}" installed depth`}
          note="Including handle"
        />
      </Card>

      <div className="mt-4 bg-primary/5 border border-primary/15 rounded-xl p-3 text-sm text-foreground">
        <b>What's next:</b> We'll do a quick scan of your countertop area and under the sink to check a few things the model specs can't tell us.
      </div>
    </AppShell>
  )
}
